import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import React, { forwardRef } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import styles from './content-search-bar-input.module.scss';

export interface ContentSearchBarInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** Indicates whether the field has a clear button */
  hasClear?: boolean;
  /** The icon to show inside the input field */
  icon?: IconName;
  /** The text that is shown above the field as a label */
  label?: string;
  /** An event that is triggered on blur */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** An event that is triggered on change */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** An event that is triggered on clear */
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** An event that is triggered on click */
  onClick?: () => void;
  /** An event that is triggered on pressing enter key */
  onEnter?: () => void;
  /** An event that is triggered on focus */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** The placeholder for the input field */
  placeholder?: string;
  /** The url to link to */
  url?: string;
  /** The value of the field */
  value?: string;
}

const ContentSearchBarInput = forwardRef<
  HTMLInputElement,
  ContentSearchBarInputProps
>(
  (
    {
      className,
      disabled,
      hasClear,
      icon,
      id,
      label,
      onClear,
      onClick,
      onEnter,
      placeholder,
      url,
      value,
      ...rest
    },
    ref,
  ) => {
    const { t } = useI18n();

    const showClear = hasClear && value && value.length > 0;

    return (
      <div
        className={classNames(styles.component, className, {
          [styles.showClear]: showClear,
          [styles.hasIcon]: icon,
          [styles.hasLabel]: label,
        })}
      >
        <div className={styles.fieldWrapper}>
          {icon && <Icon className={styles.icon} name={icon} size={24} />}

          {label && (
            <Typography
              className={styles.label}
              htmlFor={id}
              tag="label"
              tagStyle="bodySmallBold"
            >
              {label}
            </Typography>
          )}

          <div className={styles.buttonsWrapper}>
            {showClear && (
              <button
                aria-label={t('items.common.words.close')}
                className={styles.closeIconButton}
                onClick={onClear}
              >
                <Icon name="Close" size={24} />
              </button>
            )}

            <Button
              aria-label={t('items.common.words.search')}
              className={styles.button}
              onClick={onClick}
              text={t('items.common.words.search')}
              url={url}
              variant="primary"
            />
          </div>

          <input
            {...rest}
            ref={ref}
            className={styles.field}
            disabled={disabled}
            id={id}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                onEnter && onEnter();
              }
            }}
            placeholder={placeholder}
            value={value}
          />
        </div>
      </div>
    );
  },
);

ContentSearchBarInput.displayName = 'ContentSearchBarInput';

export default ContentSearchBarInput;
